import * as React from 'react'
import { useSearchParams } from 'react-router'
import { parseUuid } from '@lib/validators/uuid.validator'
import logger, { toErrorMessage } from '@/helpers/logger'

/** Whether the tray was last left open on this device. */
const STORAGE_KEY = 'swocrm.bidTray.open'

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch (error) {
    logger.warn('Could not read whether the bid tray was open', {
      error: toErrorMessage(error),
    })
    return false
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(open))
  } catch (error) {
    logger.warn('Could not remember whether the bid tray is open', {
      error: toErrorMessage(error),
    })
  }
}

export interface BidTray {
  open: boolean
  /** Opened or closed by the technician, and remembered on this device. */
  toggle: () => void
  /** Closed to make room, without changing what is remembered. */
  close: () => void
  /** The proposal the technician came from, from `?proposal=`. */
  requestedProposalId: string | null
  /** Add up another draft instead, and keep it in the address. */
  chooseProposal: (proposalId: string) => void
}

/**
 * The bid tray's state on a survey screen.
 *
 * Which bid rides in the address (`?proposal=`), so the proposal page can
 * link to a site's survey and the tray adds up that proposal, and switching
 * to another site of the bid keeps it. Whether the tray is open is a habit,
 * so it is kept per device; closing it to make room for an editor on a phone
 * is not a choice about the habit and is not remembered.
 */
export function useBidTray(): BidTray {
  const [params, setParams] = useSearchParams()
  const [open, setOpen] = React.useState(readOpen)

  const toggle = React.useCallback(() => {
    setOpen((current) => {
      writeOpen(!current)
      return !current
    })
  }, [])
  const close = React.useCallback(() => {
    setOpen(false)
  }, [])
  const chooseProposal = React.useCallback(
    (proposalId: string) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          next.set('proposal', proposalId)
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  return {
    open,
    toggle,
    close,
    requestedProposalId: parseUuid(params.get('proposal')),
    chooseProposal,
  }
}
